import type { Subscription } from 'types/product/Subscription';
import type { Variant } from 'types/product/Variant';

import { orgId, subscriptionOfferId, variantOfferId } from './helpers/ids';

type Availability =
  'https://schema.org/InStock' | 'https://schema.org/OutOfStock';

// A concrete Offer shape. schema-dts's `Offer` degrades to `any` when read back
// (its property values are loosely typed), which trips the no-unsafe-* lint
// rules at every call site. Declaring our own shape keeps the offers list
// strongly typed; it remains assignable to a schema-dts Product's `offers`.
export interface JsonLdOffer {
  '@type': 'Offer';
  '@id': string;
  name: string;
  sku?: string;
  priceCurrency: string;
  price: string;
  availability: Availability;
  priceValidUntil: string;
  url: string;
  seller: { '@id': string };
}

const availabilityUrl = (availableForSale: boolean): Availability =>
  availableForSale
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

// "Default Title" is Shopify's placeholder for a single, unnamed variant.
// Returns the variant title when meaningful, otherwise the fallback.
export const variantTitleOr = (variant: Variant, fallback: string): string =>
  variant.title && variant.title !== 'Default Title' ? variant.title : fallback;

// A one-time-purchase Offer for a variant. The display `name` is decided by the
// caller (naming policy differs by category). No priceSpecification - OTP and
// subscription offers are distinguished by their human-readable name.
export const buildOtpOffer = (
  variant: Variant,
  pageUrl: string,
  pagePath: string,
  currency: string,
  priceValidUntil: string,
  name: string,
): JsonLdOffer => ({
  '@type': 'Offer',
  '@id': variantOfferId(pagePath, variant.id),
  name,
  ...(variant.sku ? { sku: variant.sku } : {}),
  priceCurrency: currency,
  price: variant.price.amount.toString(),
  availability: availabilityUrl(variant.availableForSale),
  priceValidUntil,
  url: pageUrl,
  seller: { '@id': orgId() },
});

// A subscription Offer. The display `name` is decided by the caller.
export const buildSubscriptionOffer = (
  sub: Subscription,
  pageUrl: string,
  pagePath: string,
  currency: string,
  priceValidUntil: string,
  name: string,
): JsonLdOffer => ({
  '@type': 'Offer',
  '@id': subscriptionOfferId(pagePath, sub.id, sub.sellingPlan.id),
  name,
  ...(sub.sku ? { sku: sub.sku } : {}),
  priceCurrency: currency,
  price: sub.price.amount.toString(),
  availability: availabilityUrl(sub.availableForSale),
  priceValidUntil,
  url: pageUrl,
  seller: { '@id': orgId() },
});
